import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  LineChart,
  Select,
  SegmentedControl,
  SpaceBetween,
  Table,
  Toggle,
  type TableProps,
} from '@cloudscape-design/components';
import { PageLayout, PageSkeleton, EmptyState, useAuth, useRoles, useNotify, createApiClient } from '@deloitte-us-consulting/gp-sdk';
import { dayToDate, formatCompact, yDomainFor } from '../usageTrend';
import { ChangeText } from './ChangeText';
import { PlatformTeamModal, toTeamBody } from './PlatformTeamModal';
import { TargetDrilldown } from './TargetDrilldown';
import { byNumber, byText, useTableSort } from './useTableSort';
import {
  changeText,
  formatCount,
  formatPerPerson,
  groupTargets,
  SECTION_TITLES,
  summaryPath,
  targetLabel,
  type PeriodKind,
  type RegistryApp,
  type Section,
  type TargetRow,
  type Team,
  type TeamBody,
  type UsagePerson,
  type UsageSummary,
} from './usageApi';

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

/** The day App Store visits started being counted (the usage-tracking deploy); nothing before it is recorded. */
const COUNTING_BEGAN = 'October 22, 2026';

const KIND_OPTIONS = [
  { id: 'month', text: 'Month' },
  { id: 'quarter', text: 'Fiscal quarter' },
  { id: 'year', text: 'Fiscal year' },
];

const CHART_METRICS = [
  { id: 'uniques', text: 'Unique people' },
  { id: 'accesses', text: 'Accesses' },
] as const;

const shortMonth = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
const utc = (iso: string) => new Date(`${iso}T00:00:00Z`);
const formatDay = (d: Date, withYear: boolean) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });

/** "Sep 1–Nov 30, 2026", "Oct 1–31, 2026", or "Jun 1, 2026–May 31, 2027" across a year end. */
function formatRange(startIso: string, endIso: string): string {
  const start = utc(startIso);
  const end = utc(endIso);
  const year = end.getUTCFullYear();
  if (start.getUTCFullYear() !== year) return `${formatDay(start, true)}–${formatDay(end, true)}`;
  if (start.getUTCMonth() === end.getUTCMonth()) return `${formatDay(start, false)}–${end.getUTCDate()}, ${year}`;
  return `${formatDay(start, false)}–${formatDay(end, false)}, ${year}`;
}

/**
 * App Store usage for platform admins: who visited the App Store, each app and each tool, by
 * fiscal period. Every number comes from GET /api/usage/summary; the page never computes one.
 */
export default function UsageAdmin() {
  const { accessToken } = useAuth();
  const { can } = useRoles();
  const isAllowed = can('Usage:Manage', { appId: '*' });
  const { notify } = useNotify();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);

  // What the next summary request asks for. Empty means the server's default (the current quarter).
  const [request, setRequest] = useState<{ kind?: PeriodKind; id?: string }>({});
  const [includeTeam, setIncludeTeam] = useState(false);
  const [reload, setReload] = useState(0);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const [apps, setApps] = useState<RegistryApp[] | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drill, setDrill] = useState<string | null>(null);
  const [metric, setMetric] = useState<'uniques' | 'accesses'>('uniques');

  // Names come from the full registry, so a disabled app still shows its name.
  // Only an id missing from it gets the "No longer in the registry" badge.
  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<RegistryApp[]>('/api/registry/all');
        if (!cancelled) setApps(data);
      } catch {
        // Names fall back to ids, and nothing gets the removed badge.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, isAllowed]);

  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<Team>('/api/usage/team');
        if (!cancelled) setTeam(data);
      } catch (e) {
        if (!cancelled) notify({ type: 'error', header: 'Could not load the platform team', content: (e as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
    // Not depending on `notify`: it is a fresh function each render.
  }, [api, isAllowed]);

  useEffect(() => {
    if (!isAllowed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await api.get<UsageSummary>(summaryPath(request.kind, request.id, includeTeam));
        if (!cancelled) setSummary(data);
      } catch (e) {
        if (!cancelled) notify({ type: 'error', header: 'Could not load App Store usage', content: (e as Error).message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, isAllowed, request, includeTeam, reload]);

  const saveTeam = async (body: TeamBody) => {
    setSaving(true);
    try {
      setTeam(await api.put<Team>('/api/usage/team', body));
      setTeamOpen(false);
      setReload((n) => n + 1);
      notify({ type: 'success', header: 'Platform team saved', content: 'Saved. The numbers now use the updated platform team.' });
    } catch (e) {
      notify({ type: 'error', header: 'Could not save the platform team', content: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const addToTeam = (person: UsagePerson) => {
    if (!team) return;
    void saveTeam(toTeamBody([...team.members, { email: person.email, ...(person.name ? { name: person.name } : {}) }]));
  };

  if (!isAllowed) {
    return (
      <PageLayout title="App Store usage">
        <EmptyState title="Platform admin access required" description="Ask an admin to grant you the Admin role." />
      </PageLayout>
    );
  }
  if (!summary) {
    return (
      <PageLayout title="App Store usage">
        {loading ? <PageSkeleton label="Loading App Store usage…" /> : (
          <EmptyState title="Couldn't load App Store usage" description="Check that the platform API is deployed and you hold the Admin role, then reload." />
        )}
      </PageLayout>
    );
  }

  const { period, previous, store } = summary;
  const groups = groupTargets(summary.targets, apps ?? [], apps !== null);
  // Derived from the latest summary, so the modal stays in step after a team change reloads it.
  const zero = { uniques: 0, accesses: 0, perPerson: 0, regulars: 0 };
  const drillRow = drill
    ? (groups.flatMap((g) => g.rows).find((r) => r.target === drill) ?? { target: drill, name: targetLabel(drill, apps ?? []).name, removed: false, current: zero, previous: zero })
    : null;
  const periodOptions = summary.periods.map((p) => ({ value: p.id, label: p.label, ...(p.closed ? {} : { description: 'In progress' }) }));
  const selectedPeriod = periodOptions.find((o) => o.value === period.id) ?? { value: period.id, label: period.label };

  const tiles = [
    { label: 'Unique people', value: formatCount(store.current.uniques), change: changeText(store.current.uniques, store.previous.uniques) },
    { label: 'Accesses', value: formatCount(store.current.accesses), change: changeText(store.current.accesses, store.previous.accesses) },
    { label: 'Accesses per person', value: formatPerPerson(store.current.perPerson), change: changeText(store.current.perPerson, store.previous.perPerson) },
    { label: 'Regulars', value: formatCount(store.current.regulars), change: changeText(store.current.regulars, store.previous.regulars) },
  ];

  const trendMax = summary.trend.reduce((n, p) => Math.max(n, p[metric]), 0);
  const first = summary.trend[0];
  const last = summary.trend[summary.trend.length - 1];
  const metricText = CHART_METRICS.find((x) => x.id === metric)?.text ?? '';

  return (
    <PageLayout
      title="App Store usage"
      description={`${period.label} · ${formatRange(period.start, period.end)}${period.closed ? '' : ' · in progress'}, compared with ${previous.label}.`}
    >
      <SpaceBetween size="l">
        <Alert type="info">
          {`Usage counting began on ${COUNTING_BEGAN}. Earlier periods show no visits, so comparisons with them aren't meaningful yet.`}
        </Alert>
        <SpaceBetween direction="horizontal" size="l" alignItems="center">
          <SegmentedControl
            label="Period type"
            selectedId={request.kind ?? period.kind}
            options={KIND_OPTIONS}
            onChange={({ detail }) => setRequest({ kind: detail.selectedId as PeriodKind })}
          />
          <Select
            ariaLabel="Period"
            selectedOption={selectedPeriod}
            options={periodOptions}
            onChange={({ detail }) => {
              const picked = summary.periods.find((p) => p.id === detail.selectedOption.value);
              if (picked) setRequest({ kind: picked.kind, id: picked.id });
            }}
          />
          <Toggle checked={includeTeam} onChange={({ detail }) => setIncludeTeam(detail.checked)}>
            Include platform team
          </Toggle>
          <Button onClick={() => setTeamOpen(true)} disabled={!team}>
            {`Platform team (${team?.members.length ?? 0})`}
          </Button>
        </SpaceBetween>

        <Container>
          <SpaceBetween size="m">
            <ColumnLayout columns={4} variant="text-grid">
              {tiles.map((t) => (
                <div key={t.label}>
                  <Box variant="awsui-key-label">{t.label}</Box>
                  <Box variant="awsui-value-large">{t.value}</Box>
                  <ChangeText change={t.change} soFar={!period.closed} />
                </div>
              ))}
            </ColumnLayout>
            <Box color="text-body-secondary" fontSize="body-s">
              Accesses count visits to the App Store, once per person per 30 minutes. Regulars used it in 3 of the last 4 weeks.
            </Box>
            {!period.closed && (
              <Box color="text-body-secondary" fontSize="body-s">
                This period is still in progress, so changes compare it so far with the whole previous period.
              </Box>
            )}
          </SpaceBetween>
        </Container>

        <Container header={<Header variant="h2">Trend, last 13 months</Header>}>
          <SpaceBetween size="s">
            {/* One metric at a time: people and accesses are on very different scales and a chart has one y-axis. */}
            <SegmentedControl
              label="Metric"
              selectedId={metric}
              options={CHART_METRICS.map((x) => ({ ...x }))}
              onChange={({ detail }) => setMetric(detail.selectedId as 'uniques' | 'accesses')}
            />
            {!first || !last || summary.trend.every((p) => p.uniques === 0 && p.accesses === 0) ? (
              <Box textAlign="center" color="text-body-secondary" padding="l">
                No usage recorded in this period.
              </Box>
            ) : (
              <LineChart
                series={[
                  {
                    title: metricText,
                    type: 'line',
                    data: summary.trend.map((p) => ({ x: dayToDate(`${p.month}-01`), y: p[metric] })),
                    valueFormatter: (y) => y.toLocaleString('en-US'),
                  },
                ]}
                xScaleType="time"
                xDomain={[dayToDate(`${first.month}-01`), dayToDate(`${last.month}-01`)]}
                yDomain={yDomainFor(trendMax)}
                xTitle="Month (UTC)"
                yTitle={metricText}
                height={280}
                hideFilter
                hideLegend
                ariaLabel={`${metricText} per month`}
                empty={<Box color="text-body-secondary">No data.</Box>}
                i18nStrings={{ xTickFormatter: shortMonth, yTickFormatter: formatCompact }}
              />
            )}
          </SpaceBetween>
        </Container>

        {groups.map(({ section, rows }) => (
          <TargetTable key={section} section={section} rows={rows} loading={loading} onOpen={setDrill} />
        ))}
      </SpaceBetween>

      {drillRow && (
        <TargetDrilldown
          row={drillRow}
          period={period}
          api={api}
          teamVersion={team?.updatedAt ?? ''}
          adding={saving || !team}
          onAdd={addToTeam}
          onDismiss={() => setDrill(null)}
        />
      )}
      {teamOpen && team && (
        <PlatformTeamModal team={team} api={api} saving={saving} onSave={(body) => void saveTeam(body)} onDismiss={() => setTeamOpen(false)} />
      )}
    </PageLayout>
  );
}

/** Change in unique people as a number to sort on: "New" above every rise, unchanged at zero. */
const peopleChange = (r: TargetRow) =>
  r.previous.uniques === 0 ? (r.current.uniques > 0 ? Infinity : 0) : (r.current.uniques - r.previous.uniques) / r.previous.uniques;

/** One section's targets, sortable by any column; starts most unique people first, as groupTargets orders them. */
function TargetTable({
  section,
  rows,
  loading,
  onOpen,
}: {
  section: Section;
  rows: TargetRow[];
  loading: boolean;
  onOpen: (target: string) => void;
}) {
  const columns: TableProps.ColumnDefinition<TargetRow>[] = [
    {
      id: 'name',
      header: 'Name',
      sortingField: 'name',
      sortingComparator: byText((r) => r.name),
      cell: (r) => (
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Button variant="inline-link" onClick={() => onOpen(r.target)}>
            {r.name}
          </Button>
          {r.removed && <Badge color="grey">No longer in the registry</Badge>}
        </SpaceBetween>
      ),
    },
    { id: 'uniques', header: 'Unique people', sortingField: 'uniques', sortingComparator: byNumber((r) => r.current.uniques), cell: (r) => formatCount(r.current.uniques) },
    { id: 'accesses', header: 'Accesses', sortingField: 'accesses', sortingComparator: byNumber((r) => r.current.accesses), cell: (r) => formatCount(r.current.accesses) },
    { id: 'per', header: 'Per person', sortingField: 'per', sortingComparator: byNumber((r) => r.current.perPerson), cell: (r) => formatPerPerson(r.current.perPerson) },
    { id: 'regulars', header: 'Regulars', sortingField: 'regulars', sortingComparator: byNumber((r) => r.current.regulars), cell: (r) => formatCount(r.current.regulars) },
    {
      id: 'change',
      header: 'People vs last period',
      sortingField: 'change',
      sortingComparator: byNumber(peopleChange),
      cell: (r) => <ChangeText change={changeText(r.current.uniques, r.previous.uniques)} />,
    },
  ];
  const sort = useTableSort(rows, columns, { field: 'uniques', descending: true });

  return (
    <Table
      variant="container"
      header={<Header variant="h2" counter={`(${rows.length})`}>{SECTION_TITLES[section]}</Header>}
      {...sort}
      trackBy="target"
      loading={loading}
    />
  );
}
